import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  Icon,
  useColorModeValue,
  Card,
  CardBody,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Tag,
  HStack,
} from '@chakra-ui/react';
import { FiTrendingUp, FiSearch, FiBarChart2, FiDollarSign, FiStar } from 'react-icons/fi';
import FundSearch from '../components/FundSearch';

const Feature = ({ title, text, icon }) => {
  const bg = useColorModeValue('white', 'gray.800');
  return (
    <Card bg={bg}>
      <CardBody>
        <VStack spacing={4} align="center">
          <Icon as={icon} w={8} h={8} color="brand.500" />
          <Heading size="md">{title}</Heading>
          <Text textAlign="center" color={useColorModeValue('gray.600', 'gray.300')}>
            {text}
          </Text>
        </VStack>
      </CardBody>
    </Card>
  );
};

const FundCard = ({ name, category, rating, returns, risk, aum }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  
  return (
    <Box
      bg={cardBg}
      p={6}
      borderRadius="xl"
      shadow="lg"
      transition="all 0.3s"
      _hover={{ transform: 'translateY(-5px)', shadow: 'xl' }}
    >
      <VStack align="start" spacing={4}>
        <HStack justify="space-between" w="full">
          <Badge colorScheme="brand" fontSize="sm">
            {category}
          </Badge>
          <HStack>
            {Array(5).fill('').map((_, i) => (
              <Icon
                key={i}
                as={FiStar}
                color={i < rating ? 'yellow.400' : 'gray.300'}
                w={4}
                h={4}
              />
            ))}
          </HStack>
        </HStack>
        
        <Heading size="md">{name}</Heading>
        
        <SimpleGrid columns={2} spacing={4} w="full">
          <VStack align="start">
            <Text fontSize="sm" color="gray.500">1Y Returns</Text>
            <HStack>
              <Icon as={FiTrendingUp} color="green.500" />
              <Text fontWeight="bold" color="green.500">{returns}%</Text>
            </HStack>
          </VStack>
          
          <VStack align="start">
            <Text fontSize="sm" color="gray.500">Risk Level</Text>
            <Tag colorScheme={
              risk === 'High' ? 'red' :
              risk === 'Moderate' ? 'yellow' :
              'green'
            }>
              {risk}
            </Tag>
          </VStack>
          
          <VStack align="start">
            <Text fontSize="sm" color="gray.500">AUM</Text>
            <Text fontWeight="bold">₹{aum} Cr</Text>
          </VStack>
        </SimpleGrid>
      </VStack>
    </Box>
  );
};

const Funds = () => {
  const bgGradient = useColorModeValue(
    'linear(to-r, brand.600, brand.400)',
    'linear(to-r, brand.900, brand.700)'
  );
  const sectionBg = useColorModeValue('gray.50', 'gray.900');
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const gradientText = useColorModeValue(
    'linear(to-r, brand.500, brand.600)',
    'linear(to-r, brand.400, brand.500)'
  );

  const funds = [
    {
      name: 'Growth Fund Direct',
      category: 'Equity',
      rating: 4,
      returns: 15.8,
      risk: 'High',
      aum: '1,234',
    },
    {
      name: 'Balanced Advantage Fund',
      category: 'Hybrid',
      rating: 5,
      returns: 12.5,
      risk: 'Moderate',
      aum: '2,567',
    },
    {
      name: 'Blue Chip Fund',
      category: 'Equity',
      rating: 4,
      returns: 13.7,
      risk: 'High',
      aum: '3,890',
    },
    {
      name: 'Corporate Bond Fund',
      category: 'Debt',
      rating: 3,
      returns: 7.2,
      risk: 'Low',
      aum: '956',
    },
    {
      name: 'Mid Cap Opportunities',
      category: 'Equity',
      rating: 5,
      returns: 18.4,
      risk: 'High',
      aum: '1,678',
    },
    {
      name: 'Dynamic Bond Fund',
      category: 'Debt',
      rating: 4,
      returns: 8.5,
      risk: 'Low',
      aum: '2,345',
    }
  ];

  return (
    <Box bg={bgColor} minH="100vh">
      {/* Hero Section */}
      <Box 
        bg={useColorModeValue('white', 'gray.800')} 
        py={12}
        borderBottom="1px"
        borderColor={useColorModeValue('gray.100', 'gray.700')}
      >
        <Container maxW="container.xl">
          <VStack spacing={4} align="center" textAlign="center">
            <Badge colorScheme="brand" fontSize="sm" px={3} py={1}>
              Mutual Funds
            </Badge>
            <Heading 
              size="2xl" 
              bgGradient={gradientText}
              bgClip="text"
            >
              Explore Investment Opportunities
            </Heading>
            <Text 
              fontSize="xl" 
              color={useColorModeValue('gray.600', 'gray.400')}
              maxW="2xl"
            >
              Discover a wide range of mutual funds tailored to meet your investment goals
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Search and Filter Section */}
      <Container maxW="container.xl" py={8}>
        <HStack spacing={4} mb={8}>
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <Icon as={FiSearch} color="gray.400" />
            </InputLeftElement>
            <Input 
              placeholder="Search funds..." 
              bg={useColorModeValue('white', 'gray.800')}
            />
          </InputGroup>
          <Select 
            placeholder="Category" 
            bg={useColorModeValue('white', 'gray.800')}
            w="200px"
          >
            <option value="equity">Equity</option>
            <option value="debt">Debt</option>
            <option value="hybrid">Hybrid</option>
          </Select>
          <Select 
            placeholder="Risk Level" 
            bg={useColorModeValue('white', 'gray.800')}
            w="200px"
          >
            <option value="low">Low</option>
            <option value="moderate">Moderate</option>
            <option value="high">High</option>
          </Select>
        </HStack>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
          {funds.map((fund, index) => (
            <FundCard key={index} {...fund} />
          ))}
        </SimpleGrid>
      </Container>
    </Box>
  );
};

export default Funds;
